import { sanitizeHtml, hasText, htmlToText, isUsableUrl } from "@1-now/booking-core";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import imgFallback from "images/designB/imgsection.jpg";
import img1Fallback from "images/designB/imgsection1.png";
import img2Fallback from "images/designB/imgsection2.png";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { useInView } from "designs/DesignB/hooks/useInView";
import { uniqueFleetClassNames } from "utils/fleetClasses";

type Panel = {
  image_url?: string | null;
  heading?: string | null;
  description?: string | null;
  link?: string | null;
};

const FALLBACK_IMAGES = [imgFallback, img1Fallback, img2Fallback];

function ImageSection() {
  const { ourVehicles, imageSection } = useData();
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const heroEntrance = useInView<HTMLDivElement>();
  const subPanelsEntrance = useInView<HTMLDivElement>();

  // Order panels by `index` so the admin's drag-sort applies; fall back to
  // the order the API returned. Pad to 3 so the layout never collapses.
  const panels: Panel[] = useMemo(() => {
    const sorted = [...(imageSection?.keypoints ?? [])].sort(
      (a: any, b: any) => (a.index ?? 0) - (b.index ?? 0),
    );
    while (sorted.length < 3) sorted.push({});
    return sorted.slice(0, 3) as Panel[];
  }, [imageSection]);

  const heroPanel = panels[0];
  const heroDescription = heroPanel?.description ?? "";
  const isTruncated = heroDescription.length > 500;
  const shortDescription = isTruncated
    ? heroDescription.slice(0, 500)
    : heroDescription;

  const brands = useMemo(() => {
    const seen = new Set<string>();
    return uniqueFleetClassNames(ourVehicles)
      .map((name) => {
        if (seen.has(name.toLowerCase())) return null;
        seen.add(name.toLowerCase());
        return { name, isDefault: false };
      })
      .filter(Boolean) as { name: string; logo: string; isDefault: boolean }[];
  }, [ourVehicles]);

  const goTo = (link?: string | null) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.open(link, "_blank", "noopener");
    } else {
      navigate(link);
    }
  };

  return (
    <>
      {/* ================ HERO PANEL ================ */}
      <div
        ref={heroEntrance.ref}
        className={`group relative w-full h-[440px] md:h-[500px] lg:h-[560px] overflow-hidden rounded-xl
          transform-gpu transition-all duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform
          ${
            heroEntrance.inView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-12"
          }`}
      >
        {/* Image with subtle kenburns — runs continuously, intensifies slightly on hover. */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={isUsableUrl(heroPanel?.image_url) ? heroPanel.image_url : FALLBACK_IMAGES[0]}
            alt={htmlToText(heroPanel?.heading) || "Image section hero"}
            className="w-full h-full object-cover scale-105 transition-transform duration-[14000ms] ease-out group-hover:scale-[1.12]"
          />
          {/* Gradient overlay — original framing for legibility, plus a soft
              top vignette on hover for depth. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.05) 25%, rgba(0, 0, 0, 0.62) 100%)",
            }}
          />
          <div className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100 bg-gradient-to-b from-black/30 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-10 lg:p-16">
          {/* Heading — slides up after the card itself. */}
          <div
            className={`max-w-3xl mt-auto pt-20 transform-gpu transition-all duration-[900ms] delay-200 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${
                heroEntrance.inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
          >
            {/* h2, matching the sibling panels below. As an h1 this was a
                second top-level heading on the home page competing with the
                hero, and it is a card title in a grid, not the page's subject. */}
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-[-0.015em] mb-5 lg:mb-6
                [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(heroPanel?.heading || "") }}
            />
          </div>

          <div>
            {/* Brand pills — staggered cascade on entrance, lift + glow on hover. White chips over
                the photo, so the label is a fixed dark ink (1NOW-11217): `text-text-color` is the
                page's text colour, which is cream in a dark palette, and cream on white read as
                empty boxes. */}
            {brands.length > 0 && (
              <div className="flex flex-wrap gap-2.5 sm:gap-3 mb-6 sm:mb-8">
                {brands.slice(0, 6).map((brand, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => navigate("/vehicles?class=" + encodeURIComponent(brand.name))}
                    style={{
                      transitionDelay: heroEntrance.inView
                        ? `${500 + index * 90}ms`
                        : "0ms",
                    }}
                    className={`relative px-4 py-2 bg-white/95 text-gray-900 font-semibold text-sm rounded-lg cursor-pointer
                      transform-gpu transition-all duration-500 ease-out
                      hover:bg-white hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/50
                      ${
                        heroEntrance.inView
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4"
                      }`}
                  >
                    {brand.name}
                  </button>
                ))}
              </div>
            )}

            {/* Description with view-more toggle */}
            {heroDescription && (
              <div
                className={`transform-gpu transition-all duration-700 delay-[700ms] ease-out
                  ${
                    heroEntrance.inView
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
              >
                <span className="block !font-normal !text-md text-white/90 break-words text-wrap leading-relaxed
                  [&_p]:mb-2 last:[&_p]:mb-0
                  [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                  [&_strong]:font-semibold [&_em]:italic [&_u]:underline">
                  {expanded || !isTruncated ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(heroDescription) }}
                    />
                  ) : (
                    <>
                      <span
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(shortDescription) }}
                      />
                      ...
                    </>
                  )}
                  {isTruncated && (
                    <button
                      type="button"
                      className="ml-2 text-primary-brand hover:text-primary-brand font-semibold focus:outline-none inline transition-colors duration-200"
                      onClick={() => setExpanded((v) => !v)}
                      style={{ display: "inline" }}
                    >
                      {expanded ? "View less" : "View more"}
                    </button>
                  )}
                </span>
              </div>
            )}

            {/* Hero CTA — gradient bg, arrow nudge on hover, lift, glow. */}
            {heroPanel?.link && (
              <button
                type="button"
                onClick={() => goTo(heroPanel.link)}
                style={{
                  transitionDelay: heroEntrance.inView ? "850ms" : "0ms",
                }}
                className={`group/cta mt-5 sm:mt-6 inline-flex items-center gap-2 bg-white/95 hover:bg-white text-gray-900 px-5 py-2.5 rounded-[10px] font-semibold text-[15px]
                  transform-gpu transition-all duration-500 ease-out
                  hover:-translate-y-0.5 active:scale-[0.98]
                  ${
                    heroEntrance.inView
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
              >
                Learn more
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ================ SUB PANELS ================ */}
      <div
        ref={subPanelsEntrance.ref}
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8"
      >
        {panels.slice(1, 3).map((panel, index) => (
          <article
            key={index}
            style={{
              transitionDelay: subPanelsEntrance.inView
                ? `${index * 150}ms`
                : "0ms",
            }}
            className={`group/sub relative rounded-xl overflow-hidden h-[260px] cursor-pointer
              transform-gpu transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]
              hover:shadow-lg
              ${
                subPanelsEntrance.inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-12"
              }`}
            onClick={() => panel?.link && goTo(panel.link)}
          >
            {/* Image with hover zoom */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={isUsableUrl(panel?.image_url) ? panel.image_url : FALLBACK_IMAGES[index + 1]}
                alt={htmlToText(panel?.heading) || `Panel ${index + 2}`}
                className="w-full h-full object-cover transform-gpu transition-transform duration-[1200ms] ease-out group-hover/sub:scale-110"
              />
              {/* Layered gradients — base for legibility + a hover layer that
                  deepens slightly so text always stays readable as the image
                  zooms and brightens. */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              {/* The heading sits at the top: a shade there too, so white stays readable on a bright
                  photo (sky, a white wall) as well as a dark one (1NOW-11221). */}
              <div data-testid="panel-top-shade" className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/75 via-black/50 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover/sub:opacity-100" />
            </div>

            {/* Subtle accent ring — fades in on hover */}
            <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5" />

            <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-7 lg:p-8">
              <h2 className="text-base md:text-lg lg:text-xl font-bold text-white drop-shadow-lg [text-shadow:0_1px_3px_rgba(0,0,0,0.6)] leading-tight max-w-[80%]">
                {/* Builder fields are HTML: shown as their words, not their tags. */}
                {htmlToText(panel?.heading)}
              </h2>

              {hasText(panel?.description) && (
                <p className="text-sm text-white/85 line-clamp-2 max-w-md leading-relaxed">
                  {htmlToText(panel.description)}
                </p>
              )}

              {panel?.link && (
                <div className="self-end">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo(panel.link);
                    }}
                    className="group/btn flex items-center gap-1.5 bg-white/10 hover:bg-white text-white hover:text-gray-900 border border-white/40 hover:border-white px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 hover:shadow-md hover:shadow-white/20 active:scale-95"
                  >
                    MORE
                    <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </button>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

export default ImageSection;
